/**
 * Reseller messaging inbox — list of bookings with active threads and
 * a detail view that mirrors the customer-portal thread UI. Powered by
 * the v141 reseller_booking_messages schema.
 */
import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import ResellerShell from './ResellerShell'
import { useLanguage } from '../../context/LanguageContext'
import { tournamentApi } from '../../services/api'

export default function MessagesInbox() {
  const { t } = useLanguage()
  const { bookingId } = useParams()
  return (
    <ResellerShell title={bookingId ? t('sp_conversation', 'Conversation') : t('messages', 'Messages')}>
      {bookingId ? <ThreadDetail bookingId={Number(bookingId)} /> : <InboxList />}
    </ResellerShell>
  )
}

function InboxList() {
  const { t } = useLanguage()
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    tournamentApi.get('/portal/reseller/messages')
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.message || e.message))
  }, [])

  if (err) return <div style={errorBox}>{err}</div>
  if (!data) return <div style={{ color: '#64748b' }}>{t('sp_loading_inbox', 'Loading inbox...')}</div>
  if (data.threads.length === 0) return <div style={{ color: '#64748b' }}>{t('sp_no_conversations_yet', 'No customer conversations yet.')}</div>

  return (
    <>
      <p style={{ color: '#64748b', fontSize: 14, lineHeight: 1.6, maxWidth: 720 }}>
        {t('sp_messages_intro', 'Customers can message you directly from their trip portal. Replies trigger an email so they know to come back and read it.')}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
        {data.threads.map(thread => (
          <button
            key={thread.bookingId}
            onClick={() => navigate('/portal/reseller/messages/' + thread.bookingId)}
            style={{
              textAlign: 'left',
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              padding: 14,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                {t('sp_msg_booking_golfer', 'Booking #{id} · {golfer}', { id: thread.bookingId, golfer: thread.golferName || thread.golferEmail })}
                {thread.unread > 0 && <span style={unreadBadge}>{t('sp_unread_new', '{count} new', { count: thread.unread })}</span>}
              </div>
              <div style={{ color: '#94a3b8', fontSize: 11 }}>{new Date(thread.lastAt).toLocaleString()}</div>
            </div>
            <div style={{ color: '#64748b', fontSize: 13, marginBottom: 4 }}>{thread.packageName} · {thread.checkIn} → {thread.checkOut}</div>
            <div style={{ color: '#475569', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <strong>{thread.lastSender === 'customer' ? t('sp_th_customer', 'Customer') : t('you', 'You')}:</strong> {thread.lastBody}
            </div>
          </button>
        ))}
      </div>
    </>
  )
}

function ThreadDetail({ bookingId }) {
  const { t } = useLanguage()
  const [data, setData] = useState(null)
  const [reply, setReply] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)

  function reload() {
    return tournamentApi.get('/portal/reseller/messages/' + bookingId)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.message || e.message))
  }
  useEffect(() => { reload() // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId])

  async function send(e) {
    e.preventDefault()
    if (!reply.trim()) return
    setSubmitting(true); setErr(null)
    try {
      await tournamentApi.post('/portal/reseller/messages/' + bookingId, { body: reply })
      setReply('')
      await reload()
    } catch (e) {
      setErr(e?.response?.data?.message || e.message)
    } finally {
      setSubmitting(false)
    }
  }

  if (err && !data) return <div style={errorBox}>{err}</div>
  if (!data) return <div style={{ color: '#64748b' }}>{t('loading_ellipsis', 'Loading...')}</div>

  const { booking, messages } = data
  return (
    <>
      <Link to="/portal/reseller/messages" style={{ color: '#64748b', fontSize: 13, textDecoration: 'none' }}>{t('sp_all_messages_back', '← All messages')}</Link>
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <div style={{ fontWeight: 600, fontSize: 16 }}>{t('sp_msg_booking_golfer', 'Booking #{id} · {golfer}', { id: booking.id, golfer: booking.golferName || booking.golferEmail })}</div>
          <Link to={'/portal/reseller/bookings/' + booking.id} style={{ color: '#0066cc', fontSize: 12, textDecoration: 'none' }}>{t('sp_open_booking', 'Open booking →')}</Link>
        </div>
        <div style={{ color: '#64748b', fontSize: 13 }}>{booking.packageName} · {booking.checkIn} → {booking.checkOut} · {booking.status.replace(/_/g, ' ')}</div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 16 }}>
        <div style={{ maxHeight: 420, overflowY: 'auto', padding: 14, borderBottom: '1px solid #e2e8f0' }}>
          {messages.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 13 }}>{t('sp_no_messages_yet', 'No messages yet.')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {messages.map(m => (
                <div key={m.id} style={{ display: 'flex', justifyContent: m.sender === 'reseller' ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '78%',
                    padding: '8px 12px',
                    borderRadius: 10,
                    background: m.sender === 'reseller' ? '#0f172a' : '#f1f5f9',
                    color: m.sender === 'reseller' ? '#fff' : '#0f172a',
                    fontSize: 13,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.75, marginBottom: 2 }}>
                      {m.authorName || (m.sender === 'reseller' ? t('you', 'You') : booking.golferName || t('sp_th_customer', 'Customer'))}
                    </div>
                    <div>{m.body}</div>
                    <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4, textAlign: 'right' }}>
                      {new Date(m.sentAt).toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <form onSubmit={send} style={{ padding: 12, display: 'flex', gap: 8 }}>
          <textarea
            value={reply}
            onChange={e => setReply(e.target.value)}
            placeholder={t('sp_reply_placeholder', 'Reply to the customer...')}
            rows={2}
            maxLength={4000}
            style={{ flex: 1, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', resize: 'vertical' }}
          />
          <button type="submit" disabled={submitting || !reply.trim()} style={primaryBtn}>
            {submitting ? t('sp_sending_ellipsis', 'Sending...') : t('sp_msg_send_reply', 'Send reply')}
          </button>
        </form>
        {err && <div style={{ ...errorBox, margin: '0 12px 12px' }}>{err}</div>}
      </div>
    </>
  )
}

const primaryBtn = { padding: '8px 14px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const errorBox = { padding: 10, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13 }
const unreadBadge = { display: 'inline-block', marginLeft: 8, padding: '2px 7px', background: '#dc2626', color: '#fff', borderRadius: 999, fontSize: 11, fontWeight: 700 }
